import Layout from "../../layout/main";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { fetchGoldLogsMerchant, fetchGoldLogDetails } from "@/api/merchant/request.get";
import TableWallet from "@/modules/user/components/walletReports/table";
import { GoldLog, FormattedGoldLog } from "@/types/api/merchant/gold-logs.types";
import CardBoxLayout from "@/modules/user/layout/cardBoxLayout";
import StackKit from "@/shared/components/kit/stack";
import { useState } from "react";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import { Box } from "@mui/material";
import TransactionDetailsModal from "./TransactionDetailsModal";
import ContentSkeleton from "@/modules/user/components/walletReports/content.skeleton";

// Custom hooks defined outside the component
const useGoldLogsData = (page: number, perPage: number) => {
    return useQuery({
        queryKey: ['goldLogsMerchant', page, perPage],
        queryFn: () => fetchGoldLogsMerchant({ per_page: perPage, page: page }),
    });
};

const useTransactionDetails = () => {
    const [detailsData, setDetailsData] = useState<any>(null);

    const { mutate: fetchDetails, isPending: isDetailsLoading } = useMutation({
        mutationFn: fetchGoldLogDetails,
        onSuccess: (res) => {
            setDetailsData(res);
        },
        onError: (error: AxiosError<{ message: string }>) => {
            console.error('Error fetching details:', error.response?.data?.message || error.message);
        },
    });

    const fetchAndOpenModal = (id: number, onSuccess?: () => void) => {
        fetchDetails(id, {
            onSuccess: () => {
                onSuccess?.();
            }
        });
    };

    return {
        detailsData,
        isDetailsLoading,
        fetchDetails: fetchAndOpenModal
    };
};

const GoldReportsPage = () => {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { data, isLoading, error } = useGoldLogsData(page, perPage);
    const { detailsData, isDetailsLoading, fetchDetails } = useTransactionDetails();

    // Format the data for the table
    const formattedData: FormattedGoldLog[] = data?.data.list.map((item: GoldLog, index: number) => ({
        rowId: ((page - 1) * perPage) + index + 1,
        req_total_price: item.req_total_price.toLocaleString('fa-IR') + ' ریال',
        created_at: new Date(item.created_at * 1000).toLocaleDateString('fa-IR'),
        shop: item.shop,
        req_price_gold: toPersianNumber(numberSeparator(item.req_price_gold)),
        req_gold_weight: item.req_gold_weight,
        tracking_code: toPersianNumber(item.tracking_code),
        id: item.id,
        action: "details"
    })) || [];

    const dataHeader = [
        { id: "req_total_price", title: "مبلغ تراکنش" },
        { id: "created_at", title: "تاریخ و ساعت تراکنش" },
        { id: "shop", title: "نماینده" },
        { id: "req_price_gold", title: "قیمت لحظه ای طلا" },
        { id: "req_gold_weight", title: "وزن طلا (گرم)" },
        { id: "tracking_code", title: "کد رهگیری تراکنش" },
        { id: "action", title: "عملیات" },
    ];

    const paginationProps = {
        current_page: data?.data.current_page || page,
        per_page: Number(data?.data.per_page) || perPage,
        total: data?.data.total || 0
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleItemsPerPageChange = (newPerPage: number) => {
        setPerPage(newPerPage);
        setPage(1);
    };

    const handleOpenModal = (row: any) => {
        if (isDetailsLoading) return;
        setSelectedTransactionId(row.id);
        fetchDetails(row.id, () => setIsModalOpen(true));
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTransactionId(null);
    };

    if (isLoading) {
        return (
            <>
                <ContentSkeleton />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Box sx={{ p: 4, textAlign: 'center', color: '#ff4444' }}>
                    خطا در دریافت اطلاعات: {(error as AxiosError<{ message: string }>)?.response?.data?.message || error.message}
                </Box>
            </>
        );
    }

    return (
        <>
            <StackKit alignItems="start" justifyContent="start">
                <TableWallet
                    dataHeader={dataHeader}
                    dataTbody={formattedData}
                    pagination={paginationProps}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    CustomSelectOption={[20, 50, 100]}
                    onActionClick={handleOpenModal}
                    actionLoading={isDetailsLoading}
                    search={false}
                />
            </StackKit>

            <TransactionDetailsModal
                open={isModalOpen}
                onClose={handleCloseModal}
                detailsData={detailsData}
                isDetailsLoading={isDetailsLoading}
            />
        </>
    );
};

export default GoldReportsPage;